import { formatAddress, type ProfileResource } from '@medplum/core'
import { featureFlags } from '@seen/core/featureFlags'
import type { Participant } from '@seen/core/models/patients/participant'
import type { ExtendedRelatedPerson } from '@seen/core/models/relatedPerson'
import { Button, TypographyDetail, TypographySubtle } from '@seen/ui'
import { IconPencil } from '@tabler/icons-react'
import { type JSX, useState } from 'react'
import { CopyButton } from '#src/components/core/CopyButton.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { AddressesField } from '#src/components/core/form/AddressesField.tsx'
import { EditButton } from '#src/components/patient/detail-content/contact-tab/EditButton.tsx'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'

interface AddressDisplayProps {
  instance: Participant | ExtendedRelatedPerson
  profile: ProfileResource
}

function EditAddressesButton({
  instance,
}: {
  instance: Participant | ExtendedRelatedPerson
}): JSX.Element {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        aria-label="Edit addresses"
        onClick={() => setOpen(true)}
      >
        <IconPencil className="size-4" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogDisplay aria-describedby={undefined}>
          <DialogHeader asChild>
            <DialogTitle>Edit Addresses</DialogTitle>
          </DialogHeader>
          <DialogContent>
            <div
              data-fake-focus-trap="true"
              // oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- catches Radix's auto-focus on open so the first row's combobox trigger does not open with a focus ring; Tab moves to the real first control
              tabIndex={0}
              className="absolute top-[-9999px] left-[-9999px] h-0 w-0"
            />
            <AddressesField instance={instance} onDone={() => setOpen(false)} />
          </DialogContent>
        </DialogDisplay>
      </Dialog>
    </>
  )
}

export function AddressDisplay({
  instance,
  profile,
}: AddressDisplayProps): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const autocomplete = useFeatureFlag(featureFlags.addressAutocomplete)
  const address = instance.primaryAddress
  return (
    <div className="group flex items-center gap-1">
      <div className="grid flex-1">
        <TypographyDetail className="leading-none font-semibold text-zinc-950">
          Address
        </TypographyDetail>
        <TypographySubtle className="py-1.5">
          {address && formatAddress(address)}
          {!address && 'Unknown'}
        </TypographySubtle>
      </div>
      <div className="invisible flex group-hover:visible">
        <CopyButton
          value={address ? formatAddress(address) : undefined}
          ariaLabel="Copy address"
        />
        {!isReadOnly &&
          (autocomplete ? (
            <EditAddressesButton instance={instance} />
          ) : (
            <EditButton profile={profile} field="address" />
          ))}
      </div>
    </div>
  )
}
